'use client';

import { useRouter } from '@bprogress/next/app';
import { useSelector } from '@xstate/store/react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';

import CartIcon from '@/fo/icons/base/tabbar/cart.svg';
import FavoriteIcon from '@/fo/icons/base/tabbar/favorite.svg';
import LogoIcon from '@/fo/icons/base/tabbar/logo.svg';
import SearchIcon from '@/fo/icons/base/tabbar/search.svg';
import UserIcon from '@/fo/icons/base/tabbar/user.svg';
import {
  storeProduct,
  selectCounterCart,
  selectCounterWishlist
} from '@/fo/store/product';
import { UiAppMenuMobile, UiAppMenuMobileProps } from '@/fo/ui/app/menu/mobile';
import { UiKitCounterBase } from '@/fo/ui/kit/counter/base';
import { useMediaQuery } from '@/fo/ui/mod/media/query';
import { utilEventAuthModal } from '@/fo/util/event-auth-modal';

import { TAPBAR_MOBILE_HEIGHT_CSS_VAR, TAPBAR_MOBILE_ID } from '../constants';

import { styles } from './UiAppTapbarMobile.styles';

export interface UiAppTapbarMobileProps {
  isAuthorized?: boolean;
  children?: React.ReactNode;
  menuProps?: Omit<UiAppMenuMobileProps, 'className'>;
  searchInput?: React.ReactNode;
  searchSuggestion?: React.ReactNode;
  loyaltyBalance?: number;
}

/** Собирает SEO-ссылки из дерева меню */
const collectSeoLinks = (
  items: UiAppMenuMobileProps['items'] = [],
  result: { href: string; label: string }[] = []
) => {
  items.forEach((item) => {
    result.push({ href: item.href, label: item.label });
    if (item.children?.length) {
      collectSeoLinks(item.children, result);
    }
  });
  return result;
};

/** Мобильный, навигационный бар, для десктопа скрыт на уровне стилей */
export function UiAppTapbarMobile({
  isAuthorized = false,
  children,
  menuProps,
  searchInput,
  searchSuggestion,
  loyaltyBalance
}: UiAppTapbarMobileProps) {
  const t = useTranslations();
  const slots = styles();
  const ref = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery('md', 'max');
  const router = useRouter();
  const [showMenu, setShowMenu] = useState(false);
  const pathname = usePathname();

  const cartCount = useSelector(storeProduct, selectCounterCart);
  const wishlistCount = useSelector(storeProduct, selectCounterWishlist);

  useEffect(() => {
    setShowMenu(false);
    const t = setTimeout(() => {
      const height = isMobile && ref.current ? ref.current.offsetHeight : 0;
      document.documentElement.style.setProperty(
        TAPBAR_MOBILE_HEIGHT_CSS_VAR,
        `${height}px`
      );
    }, 300);

    return () => clearTimeout(t);
  }, [pathname, isMobile]);

  useEffect(() => {
    document.body.style.overflow = showMenu ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [showMenu]);

  if (!isMobile) {
    return (
      <div
        className={slots.base()}
        id={TAPBAR_MOBILE_ID}
        ref={ref}
      />
    );
  }

  const handleProfileClick = () => {
    setShowMenu(false);
    if (isAuthorized) {
      router.push('/account?openMenu=true');
    } else {
      utilEventAuthModal.open('header-mobile');
    }
  };

  const handleFavoriteClick = () => {
    setShowMenu(false);
    if (isAuthorized) {
      router.push('/account/wishlist');
    } else {
      utilEventAuthModal.open('header-mobile');
    }
  };

  const seoMenuLinks = collectSeoLinks(menuProps?.items);
  const seoAdditionalLinks = collectSeoLinks(menuProps?.additionalItems);
  const seoInfoLinks =
    menuProps?.infoItems?.map((item) => ({
      href: item.href,
      label: item.label
    })) || [];
  const seoLinks = [...seoMenuLinks, ...seoAdditionalLinks, ...seoInfoLinks];

  return (
    <>
      <div
        className={slots.base()}
        id={TAPBAR_MOBILE_ID}
        ref={ref}
      >
        <nav
          className="sr-only"
          aria-label={t('SEO mobile navigation')}
        >
          {seoLinks.map((item) => (
            <Link
              key={`${item.href}-${item.label}`}
              href={item.href}
            >
              {item.label}
            </Link>
          ))}
        </nav>
        <div className={slots.container()}>
          <div
            className={slots.item()}
            onClick={() => setShowMenu((s) => !s)}
          >
            <SearchIcon />
            <span className={slots.label()}>{t('catalog')}</span>
          </div>
          <div
            className={slots.item()}
            onClick={handleFavoriteClick}
            data-mobile-app-hidden="commerce"
          >
            <div className={slots.icon()}>
              <FavoriteIcon />
              <UiKitCounterBase
                color="primary"
                className={slots.linkCounter()}
                counter={wishlistCount}
              />
            </div>
            <span className={slots.label()}>{t('favorite')}</span>
          </div>
          <Link
            href="/"
            className={slots.item()}
            onClick={() => setShowMenu(false)}
          >
            <LogoIcon />
            <span className={slots.label({ className: 'text-black' })}>
              NAOS
            </span>
          </Link>
          <div
            className={slots.item()}
            onClick={handleProfileClick}
          >
            <div className={slots.icon()}>
              <UserIcon />
              {isAuthorized && (
                <UiKitCounterBase
                  color="yellow"
                  prefix="marker"
                  className={slots.linkCounter()}
                  counter={loyaltyBalance}
                  noLimit
                />
              )}
            </div>
            <span className={slots.label()}>
              {isAuthorized ? t('Profile') : t('login')}
            </span>
          </div>
          <Link
            href="/cart"
            className={slots.item()}
            data-mobile-app-hidden="commerce"
          >
            <div className={slots.icon()}>
              <CartIcon />
              <UiKitCounterBase
                color="primary"
                className={slots.linkCounter()}
                counter={cartCount}
              />
            </div>
            <span className={slots.label()}>{t('cart.title')}</span>
          </Link>
          {showMenu && (
            <UiAppMenuMobile
              {...menuProps}
              className={slots.menu()}
              searchInput={searchInput}
              searchSuggestion={searchSuggestion}
            />
          )}
        </div>
      </div>
      {children}
    </>
  );
}
